<template>
  <v-card color="secondaryBackground">
    <!-- Title -->
    <v-card-title
      class="d-flex align-center pa-4"
      style="gap: 12px"
    >
      <v-avatar
        size="40"
        :color="`${$vuetify.theme.currentTheme.review}22`"
      >
        <v-icon color="review">
          {{ $vuetify.icons.values.alerts }}
        </v-icon>
      </v-avatar>
      <div
        class="d-flex flex-column"
        style="min-width: 0"
      >
        <span
          class="text-h6 font-weight-black"
          style="line-height: 1.2"
        >
          {{ getAlertRecord?.editAlert ? "Edit Alert" : "New Alert" }}
        </span>
        <span class="text-caption text--secondary">
          {{
            getAlertRecord?.editAlert
              ? "Update this alert's conditions"
              : "Get notified when something changes"
          }}
        </span>
      </div>
      <v-spacer />
      <v-btn
        icon
        :disabled="loading.submit"
        @click="traverse('cancel')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider />

    <v-card-text class="pt-4">
      <!-- Record context (compact) -->
      <v-sheet
        class="rounded-lg pa-3 mb-4"
        color="transparent"
        :style="{ border: `1px solid ${$vuetify.theme.currentTheme.background3}` }"
      >
        <div
          v-if="loading.record"
          class="d-flex align-center justify-center py-2"
        >
          <v-progress-circular
            indeterminate
            size="22"
            width="2"
            color="review"
          />
        </div>
        <template v-else-if="recordSummary">
          <div
            class="d-flex align-center"
            style="gap: 12px"
          >
            <v-icon color="secondaryIcon">
              {{ recordSummary.icon }}
            </v-icon>
            <div
              class="d-flex flex-column"
              style="min-width: 0"
            >
              <span class="text-caption text--secondary text-capitalize">
                {{ recordSummary.type }}
              </span>
              <span class="text-body-2 font-weight-bold text-truncate">
                {{ recordSummary.label }}
              </span>
            </div>
            <v-spacer />
            <StatusChip
              v-if="recordSummary.status"
              :status="recordSummary.status"
              no-top-margin
            />
          </div>
          <div
            v-if="recordSummary.facts.length"
            class="d-flex flex-wrap mt-3"
            style="gap: 8px 24px"
          >
            <div
              v-for="fact in recordSummary.facts"
              :key="fact.label"
              class="d-flex flex-column"
            >
              <span class="text-caption text--secondary">{{ fact.label }}</span>
              <span class="text-body-2 font-weight-medium">{{ fact.value }}</span>
            </div>
          </div>
        </template>
      </v-sheet>

      <!-- Alert config -->
      <div
        class="d-flex align-center mb-2"
        style="gap: 8px"
      >
        <v-icon
          small
          color="review"
        >
          {{ isReminder ? "mdi-clock-outline" : "mdi-bell-ring-outline" }}
        </v-icon>
        <span class="text-body-2 font-weight-black">Notify me when…</span>
      </div>
      <v-form>
        <v-row dense>
          <v-col
            cols="12"
            :sm="isReminder ? 4 : 6"
            :md="isReminder ? 4 : 3"
          >
            <v-select
              v-model="payload.alert.field"
              dense
              outlined
              :items="fields"
              :hint="`${fields.length} field${
                fields.length === 1 ? '' : 's'
              } available`"
              persistent-hint
              no-data-text="No fields"
              @change="updateFieldValue"
            >
              <template #label>
                Field to watch <span class="error--text">*</span>
              </template>
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.desc }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>
          </v-col>
          <template v-if="!isReminder">
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-select
                v-model="payload.alert.operator"
                dense
                outlined
                :hint="`${operators.length} operator${
                  operators.length === 1 ? '' : 's'
                } available`"
                persistent-hint
                :items="operators"
                :disabled="!fieldSet || operators.length === 0"
              >
                <template #label>
                  Operator
                  <span
                    v-if="operators.length"
                    class="error--text"
                  >*</span>
                </template>
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.value !== '=='">
                      {{ item.value }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-select>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-if="!fieldSet"
                label="Value to watch"
                hint="Set a field first"
                persistent-hint
                dense
                outlined
                disabled
              />
              <template v-else>
                <v-select
                  v-if="payload.alert.field === 'status'"
                  v-model="payload.alert.value"
                  dense
                  outlined
                  :items="status"
                  hide-selected
                  persistent-hint
                  :hint="`${status.length} status${
                    status.length === 1 ? '' : 'es'
                  } available`"
                >
                  <template #label>
                    Status <span class="error--text">*</span>
                  </template>
                  <template #item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-badge
                          inline
                          dot
                          class="mr-1"
                          :color="item.color"
                        />
                        <span>{{ item.text }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                  <template #selection="{ item }">
                    <v-badge
                      inline
                      dot
                      class="mr-1"
                      :color="item.color"
                    />
                    <span>{{ item.text }}</span>
                  </template>
                </v-select>
                <v-text-field
                  v-else-if="payload.alert.field === 'balanceDue'"
                  label="Balance Due"
                  :value="payload.alert.value | currency"
                  dense
                  outlined
                  disabled
                />
                <v-text-field
                  v-else-if="payload.alert.field === 'newPayment'"
                  label="Value to watch"
                  value="No value to set"
                  dense
                  outlined
                  disabled
                />
              </template>
            </v-col>
          </template>
          <template v-else>
            <v-col
              cols="12"
              sm="4"
            >
              <v-select
                v-model="payload.alert.recurrence"
                label="Recurrence"
                dense
                outlined
                :items="recurrenceOptions"
                clearable
                hint="Repeat this reminder"
                persistent-hint
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-menu
                ref="menu"
                v-model="payload.alert.date.menu"
                offset-y
                :close-on-content-click="false"
                transition="scale-transition"
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-bind="attrs"
                    :value="formattedDate"
                    prepend-inner-icon="mdi-calendar"
                    dense
                    outlined
                    readonly
                    v-on="on"
                  >
                    <template #label>
                      Date <span class="error--text">*</span>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="payload.alert.date.value"
                  color="review"
                  :min="today"
                />
              </v-menu>
            </v-col>
          </template>
        </v-row>
        <v-row
          v-if="isReminder"
          dense
        >
          <v-col cols="4">
            <v-select
              v-model="payload.alert.time.hour"
              dense
              outlined
              :items="getEventHours"
            >
              <template #label>
                Hour <span class="error--text">*</span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="payload.alert.time.minute"
              label="Minute"
              dense
              outlined
              :items="minuteOptions"
            />
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="payload.alert.timeOfDay"
              label="AM / PM"
              dense
              outlined
              :items="getEventTimeOfDay"
            />
          </v-col>
          <v-col
            v-if="reminderIsPast"
            cols="12"
            class="pt-0"
          >
            <span class="error--text caption">Reminder time must be in the future.</span>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <v-textarea
              v-model.trim="payload.alert.notes"
              label="Notes"
              outlined
              dense
              rows="2"
              auto-grow
              placeholder="Additional context for this alert…"
              persistent-placeholder
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-alert
      v-if="error"
      type="error"
      dense
      text
      class="mx-4 mb-0"
    >
      {{ error }}
    </v-alert>

    <v-divider />
    <v-card-actions class="pa-4">
      <v-btn
        text
        color="error"
        :disabled="loading.record || loading.submit"
        @click="traverse('cancel')"
      >
        Cancel
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="success"
        :loading="loading.submit"
        :disabled="invalidSubmission"
        @click="traverse('submit')"
      >
        {{ getAlertRecord?.editAlert ? "Save" : "Create" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
// Libraries
import { mapActions, mapGetters } from "vuex";
import {
  // Firestore
  firestore,
  collection,
  getDoc,
  doc,
  serverTimestamp,
  writeBatch,
} from "@/firebase/init";
import moment from "moment";
// Components
import StatusChip from "@/components/misc/ui/chips/StatusChip.vue";

export default {
  name: "Alert",
  components: {
    StatusChip,
  },
  data: () => ({
    loading: {
      record: false,
      overall: false,
      submit: false,
    },
    error: "",
    response: {
      record: null,
    },
    recurrenceOptions: [
      { text: "One-time", value: null },
      { text: "Daily", value: "daily" },
      { text: "Weekly", value: "weekly" },
      { text: "Biweekly", value: "biweekly" },
      { text: "Monthly", value: "monthly" },
    ],
    payload: {
      alert: {
        field: null,
        operator: null,
        value: null,
        notes: "",
        recurrence: null,
        date: {
          menu: false,
          value: null,
        },
        time: {
          hour: null,
          minute: "00",
        },
        timeOfDay: "pm",
      },
    },
  }),
  created() {
    this.setup();
  },
  methods: {
    ...mapActions(["_recordAlert"]),
    /* API Calls */
    // GET
    async getRecord() {
      this.loading.record = true;
      this.response.record = null;
      this.error = "";
      try {
        const recordDoc = doc(
          firestore,
          this.getAlertRecord.resource,
          this.getAlertRecord.id
        );
        const response = await getDoc(recordDoc);
        if (response.exists()) {
          this.response.record = {
            id: response.id,
            ...response.data(),
          };
        }

        if (
          this.getAlertRecord.resource === "accounts" &&
          this.getAlertRecord.brandId
        ) {
          const brandStatsDoc = doc(
            firestore,
            "brandStats",
            `${this.getAlertRecord.id}_${this.getAlertRecord.brandId}`
          );
          const brandStatsResponse = await getDoc(brandStatsDoc);
          if (brandStatsResponse.exists()) {
            this.response.record.brandStatus = brandStatsResponse.data().status;
          }
        }

        this.processRecord();
      } catch (e) {
        console.error("getRecord failed", e);
        this.error = "Failed to load record. Please try again.";
      } finally {
        this.loading.record = false;
      }
    },
    // POST
    async submit() {
      this.loading.submit = true;
      this.error = "";
      try {
        const batch = writeBatch(firestore);
        const isEditMode = !!this.getAlertRecord?.editAlert;

        let fieldDisplay = "",
          valueDisplay = "",
          currentValue = null;
        switch (this.payload.alert.field) {
          case "reminder":
            fieldDisplay = "reminder";
            valueDisplay = `${moment(this.payload.alert.date.value).format(
              "MM/DD/YYYY"
            )} @ ${this.payload.alert.time.hour}:${
              this.payload.alert.time.minute
            } ${this.payload.alert.timeOfDay.toUpperCase()}`;
            break;
          case "status":
            fieldDisplay = "status";
            valueDisplay = this.status.find(
              (option) => option.value === this.payload.alert.value
            ).text;
            break;
          case "balanceDue":
            fieldDisplay = "balance";
            valueDisplay = "$0.00";
            break;
          case "newPayment":
            fieldDisplay = "Payment";
            valueDisplay = "added";
            break;
        }

        const triggerAt =
          this.payload.alert.field === "reminder" && this.dateSet && this.hourSet
            ? moment(
                `${this.payload.alert.date.value} ${this.payload.alert.time.hour}:${this.payload.alert.time.minute} ${this.payload.alert.timeOfDay}`,
                "YYYY-MM-DD hh:mm a"
              ).toDate()
            : null;

        const conditionPayload = {
          field: this.payload.alert.field,
          fieldDisplay,
          operator: this.payload.alert.operator,
          value: this.payload.alert.value,
          valueDisplay,
          ...(triggerAt && { triggerAt }),
        };
        const reminderPayload = {
          date: this.dateSet
            ? moment(this.payload.alert.date.value).toDate()
            : "",
          time: {
            hour: this.payload.alert.time.hour || "",
            minute: this.payload.alert.time.minute || "",
            timeOfDay: this.payload.alert.timeOfDay || "",
          },
          recurrence: this.payload.alert.recurrence ?? null,
        };

        if (isEditMode) {
          const alertDoc = doc(firestore, "watchers", this.getAlertRecord.editAlert.id);
          const updatePayload = {
            notes: this.payload.alert.notes,
            condition: conditionPayload,
          };
          if (this.payload.alert.field === "reminder") {
            updatePayload.reminder = reminderPayload;
            // Re-arm: editing the trigger date on a completed reminder should reset it.
            updatePayload["flags.complete"] = false;
            updatePayload.completed = null;
          }
          batch.set(alertDoc, updatePayload, { merge: true });
        } else {
          const alertDoc = doc(collection(firestore, "watchers"));
          batch.set(
            alertDoc,
            {
              assignedTo: {
                name: this.getUser.name,
                uid: this.getUser.uid,
              },
              flags: {
                complete: false,
              },
              relatedTo: this.getAlertRecord,
              created: serverTimestamp(),
              completed: null,
              notes: this.payload.alert.notes,
              condition: conditionPayload,
              reminder: reminderPayload,
            },
            { merge: true }
          );
        }

        await batch.commit();
        this.traverse("cancel");
      } catch (e) {
        console.error("submit alert failed", e);
        this.error = "Failed to save alert. Please try again.";
      } finally {
        this.loading.submit = false;
      }
    },
    /* Main */
    async setup() {
      this.loading.overall = true;
      await this.getRecord();
      if (this.getAlertRecord?.editAlert) {
        this.prefillForEdit();
      }
      this.loading.overall = false;
    },
    prefillForEdit() {
      const ea = this.getAlertRecord.editAlert;
      this.payload.alert.field = ea.condition.field;
      this.payload.alert.operator = ea.condition.operator ?? null;
      this.payload.alert.value = ea.condition.value ?? null;
      this.payload.alert.notes = ea.notes ?? "";

      if (ea.condition.field === "reminder") {
        const parsed = moment(ea.condition.valueDisplay, "MM/DD/YYYY [@] hh:mm A");
        if (parsed.isValid()) {
          this.payload.alert.date.value = parsed.format("YYYY-MM-DD");
          this.payload.alert.time.hour = parseInt(parsed.format("h"), 10);
          this.payload.alert.time.minute = parsed.format("mm");
          this.payload.alert.timeOfDay = parsed.format("a");
        }
        this.payload.alert.recurrence = ea.reminder?.recurrence ?? null;
      }
    },
    traverse(action, params) {
      switch (action) {
        case "submit":
          this.submit();
          break;
        case "cancel":
          this._recordAlert({
            dialog: false,
            related: null,
          });
          break;
      }
    },
    processRecord() {
      if (!this.response.record) return;

      switch (this.getAlertRecord.resource) {
        case "tasks":
          this.response.record = {
            ...this.response.record,
            dueDate: moment(
              this.response.record.dueDate.seconds * 1000
            ).toDate(),
            status: this.getTaskStatus.find(
              (option) => option.value === this.response.record.status
            ),
          };
          break;
        case "salesOrders":
          this.response.record = {
            ...this.response.record,
            shipDate: this.response.record.shipDate
              ? moment(this.response.record.shipDate.seconds * 1000).format(
                  "MM/DD/YYYY"
                )
              : null,
            status: this.getOrderStatus.find(
              (option) => option.value === this.response.record.status
            ),
          };
          break;
        case "accounts":
          this.response.record = {
            ...this.response.record,
            status: this.getAccountStatus.find(
              (option) => option.value === this.response.record.brandStatus
            ),
          };
          break;
      }
    },
    updateFieldValue() {
      this.payload.alert.operator = null;

      switch (this.payload.alert.field) {
        case "status":
          this.payload.alert.value = this.response.record.status?.value ?? null;
          break;
        case "balanceDue":
          this.payload.alert.value = 0;
          break;
      }

      if (this.operators.length === 1) {
        this.payload.alert.operator = this.operators[0].value;
      }
    },
    /* Misc */
  },
  computed: {
    ...mapGetters([
      // App
      "getAppName",
      "getUser",
      "getActionListOperators",
      // Tasks
      "getTaskStatus",
      "getTaskTypes",
      // Sales Orders
      "getOrderStatus",
      // Accounts
      "getAccountStatus",
      // Alerts
      "getAlertRecord",
      "getAlertFieldTypes",
      //
      "getEventHours",
      "getEventTimeOfDay",
    ]),
    recordType() {
      let title = "",
        icon = "";

      switch (this.getAlertRecord.resource) {
        case "tasks":
          title = "task";
          icon = this.$vuetify.icons.values.task;
          break;
        case "salesOrders":
          title = "sales order";
          icon = this.$vuetify.icons.values.order;
          break;
        case "accounts":
          title = "account";
          icon = this.$vuetify.icons.values.account;
          break;
      }

      return {
        title,
        icon,
      };
    },
    isReminder() {
      return this.payload.alert.field === "reminder";
    },
    // Compact context shown at the top of the dialog.
    recordSummary() {
      const record = this.response.record;
      if (!record) return null;

      const summary = {
        type: this.recordType.title,
        icon: this.recordType.icon,
        label: this.getAlertRecord?.name || this.recordType.title,
        status: record.status ?? null,
        facts: [],
      };

      const { capitalize, currency, dateFormat } = this.$options.filters;

      switch (this.getAlertRecord.resource) {
        case "tasks":
          summary.facts = [
            {
              label: "Type",
              value:
                this.getTaskTypes.find((o) => o.value === record.type)?.text ??
                "-",
            },
            { label: "Due Date", value: dateFormat(record.dueDate) },
          ];
          break;
        case "salesOrders":
          summary.facts = [
            {
              label: "Account",
              value: capitalize(record.account?.name) || "-",
            },
            { label: "Balance", value: currency(record.balance?.due) },
          ];
          break;
        case "accounts":
          summary.facts = [
            { label: "DBA", value: record.dba ? capitalize(record.dba) : "-" },
            {
              label: "License",
              value: record.license?.number
                ? record.license.number.toUpperCase()
                : "-",
            },
          ];
          break;
      }

      return summary;
    },
    // Alert
    fields() {
      return this.getAlertFieldTypes.filter((field) =>
        field.recordTypes.includes(this.getAlertRecord.resource)
      );
    },
    operators() {
      let availableOperators = [];
      if (this.fieldSet) {
        const selectedField = this.fields.find(
          (option) => option.value === this.payload.alert.field
        );

        availableOperators = this.getActionListOperators
          .filter((operator) =>
            selectedField.operators.includes(operator.value)
          )
          .map((operator) =>
            operator.value === "=="
              ? { ...operator, text: "Is" }
              : operator
          );
      }

      return availableOperators;
    },
    status() {
      switch (this.getAlertRecord.resource) {
        case "tasks":
          return this.getTaskStatus;
          break;
        case "salesOrders":
          return this.getOrderStatus;
          break;
        case "accounts":
          return this.getAccountStatus;
          break;
        default:
          return [];
          break;
      }
    },
    // Submission
    today() {
      return moment().format("YYYY-MM-DD");
    },
    reminderIsPast() {
      if (!this.dateSet || !this.hourSet) return false;
      if (this.payload.alert.date.value !== this.today) return false;
      const chosen = moment(
        `${this.payload.alert.date.value} ${this.payload.alert.time.hour}:${this.payload.alert.time.minute} ${this.payload.alert.timeOfDay}`,
        "YYYY-MM-DD h:mm a"
      );
      return chosen.isBefore(moment());
    },
    formattedDate() {
      return this.dateSet
        ? moment(this.payload.alert.date.value).format("MM/DD/YYYY")
        : "-";
    },
    dateSet() {
      return this.payload.alert.date.value ? true : false;
    },
    hourSet() {
      return this.payload.alert.time.hour ? true : false;
    },
    fieldSet() {
      return this.payload.alert.field ? true : false;
    },
    operatorSet() {
      const selectedField = this.getAlertFieldTypes.find(
        (option) => option.value === this.payload.alert.field
      );
      if (!selectedField) return true;
      if (selectedField.operators.length > 0) {
        return this.payload.alert.operator ? true : false;
      }
      return true;
    },
    minuteOptions() {
      return Array.from({ length: 12 }, (_, i) =>
        String(i * 5).padStart(2, "0")
      );
    },
    valueSet() {
      switch (this.payload.alert.field) {
        case "status": {
          // In edit mode, allow saving (e.g. notes-only changes) even when the
          // value matches the record's current status; the new-alert guard below
          // only blocks creating an alert for the status the record is already in.
          if (this.getAlertRecord?.editAlert) {
            return this.payload.alert.value !== null;
          }
          const currentStatus = this.response.record.status?.value ?? null;
          return (
            this.payload.alert.value !== null &&
            this.payload.alert.value !== currentStatus
          );
        }
        case "balanceDue":
        case "newPayment":
          return true;
          break;
        case "reminder":
          return this.dateSet && this.hourSet && !this.reminderIsPast ? true : false;
          break;
      }
    },
    invalidSubmission() {
      return this.fieldSet && this.operatorSet && this.valueSet ? false : true;
    },
  },
};
</script>

<style></style>
